import type { Auth0Client, LogoutOptions } from '@auth0/auth0-spa-js'
import React, { useContext, useMemo, useReducer, useEffect } from 'react'
import type { Auth0Claims } from 'paintscout'
import type { Auth0Session } from './util/session'
import { clearSkipAuthCache, getCachedAuth0User, shouldSkipAuthCache } from './util/login'

export interface AuthProviderProps {
  auth0Client: Auth0Client
  scope: string
  audience: string
  appUrl?: string
  /**
   * Is called before the user is redirected to the login page.
   * Use this to clear any local data that might exist.
   */
  onLogout?: (companyId: string) => any

  children: React.ReactNode | ((props: AuthContextValue) => React.ReactNode)
}

export interface AuthContextValue {
  auth0Client?: Auth0Client
  session?: Auth0Session
  isAuthenticating: boolean

  authenticate: (opts?: { ignoreCache?: boolean }) => Promise<Auth0Session | null>
  logout: (options?: LogoutOptions) => any
  handleRedirectCallback: () => Promise<Auth0Session>
}

const AuthContext = React.createContext<AuthContextValue>({} as any)

// Grab the session stored in localstorage. shouldSkipAuthCache() and getCachedAuth0User()
// are both safeStorage-wrapped in util/login.ts, so a broken/unavailable localStorage
// (Safari ITP/private browsing/storage partitioning around the cross-origin Auth0
// redirect) resolves to "no cached session" here rather than crashing the render.
const getInitialSession = () => {
  if (shouldSkipAuthCache()) {
    return {}
  }

  return getCachedAuth0User() ?? {}
}

export default function AuthProvider({ auth0Client, onLogout, scope, audience, children, appUrl }: AuthProviderProps) {
  const cache = getInitialSession()
  const claims = cache?.decodedToken?.claims
  const expired = !!claims?.exp && claims.exp * 1000 < Date.now()
  const session = !expired && claims ? getSessionFromClaims(claims) : null

  const [state, dispatch] = useReducer(reducer, {
    isAuthenticating: true,
    session
  })

  useEffect(() => {
    const initializeAuth = async () => {
      let session: Auth0Session | null = null

      try {
        if (shouldSkipAuthCache()) {
          // After a logout or cross-app redirect, don't trust any cached session.
          // Force re-authentication. Any stale forceLogin localStorage keys are
          // harmless — consumeForceLogin() now only reads the cookie, which will
          // be present (and fresh) for a real post-logout flow.
          session = null
          clearSkipAuthCache()
        } else {
          const claims = (await auth0Client.getIdTokenClaims()) as unknown as Auth0Claims
          const expired = !!claims?.exp && claims.exp * 1000 < Date.now()
          session = !expired ? getSessionFromClaims(claims) : null
          if (session) {
            clearSkipAuthCache()
          }
        }
      } catch (err) {
        // A failure here (e.g. localStorage unavailable, or the Auth0 SDK's own cache
        // read throwing for the same reason) must not leave isAuthenticating stuck at
        // its initial `true` forever — that's a silent infinite loading state with no
        // error and no redirect. Fall through as "no session" so the app proceeds to
        // the normal not-authenticated → login flow instead.
        // eslint-disable-next-line no-console
        console.warn('[react-auth0] failed to initialize auth session', err)
        session = null
      }

      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticating: !session,
          session
        }
      })
    }

    initializeAuth()
  }, [])

  // logout when another tab logs out
  useEffect(() => {
    function listener(event: StorageEvent) {
      if (event.storageArea != localStorage) return

      if ((event.key || '').startsWith('@@auth0')) {
        // another window/tab logged in/out
        if (!event.newValue || !event.oldValue) {
          window.onbeforeunload = () => null // prevent unsaved changes alert from stopping reload, they cant save anyway
          window.location.reload()
          return
        }

        const oldUser = JSON.parse(event.oldValue)?.body?.decodedToken?.user?.sub
        const newUser = JSON.parse(event.newValue)?.body?.decodedToken?.user?.sub

        if (oldUser && newUser && oldUser !== newUser) {
          window.onbeforeunload = () => null
          window.location.reload()
          return
        }
      }
    }

    window.addEventListener('storage', listener)

    return () => {
      window.removeEventListener('storage', listener)
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      auth0Client,
      authenticate: async ({ ignoreCache } = {}) => {
        dispatch({ type: 'AUTHENTICATING' })

        try {
          const cacheModeOff = ignoreCache || !state.session
          await auth0Client.getTokenSilently({ cacheMode: cacheModeOff ? 'off' : 'on' })
          const claims = (await auth0Client.getIdTokenClaims()) as unknown as Auth0Claims
          const session = getSessionFromClaims(claims)
          if (session) {
            clearSkipAuthCache()
          }
          dispatch({ type: 'AUTHENTICATED', payload: session })

          return session
        } catch (e) {
          if (e instanceof ProgressEvent) {
            return null
          }

          throw e
        }
      },
      logout: (opts) => {
        auth0Client.logout({ ...opts, logoutParams: { federated: true, returnTo: appUrl, ...opts?.logoutParams } })
        onLogout?.(session?.app_metadata?.companyId)
        dispatch({ type: 'LOGOUT' })
      },
      handleRedirectCallback: async () => {
        await auth0Client.handleRedirectCallback()
        const claims = (await auth0Client.getIdTokenClaims()) as unknown as Auth0Claims
        const session = getSessionFromClaims(claims)
        if (session) {
          clearSkipAuthCache()
        }
        dispatch({ type: 'AUTHENTICATED', payload: session })

        return session
      }
    }),
    [auth0Client, state, audience, scope, onLogout, appUrl]
  )

  return (
    <AuthContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </AuthContext.Provider>
  )
}

interface AuthProviderState {
  isAuthenticating: boolean
  session?: Auth0Session
}

function reducer(state: AuthProviderState, action: { type: string; payload?: any }): AuthProviderState {
  const { type, payload } = action

  switch (type) {
    case 'AUTHENTICATING':
      return {
        ...state,
        isAuthenticating: true
      }
    case 'AUTHENTICATED':
      return {
        ...state,
        isAuthenticating: false,
        session: payload
      }
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticating: false
        // keep session so we don't get unexpected UI change before <Authorizer> processes the redirect
      }
    case 'UPDATE_SESSION':
      return {
        ...state,
        session: payload
      }
    case 'INITIALIZE':
      return {
        ...state,
        isAuthenticating: payload.isAuthenticating,
        session: payload.session
      }
  }

  return state
}

function getSessionFromClaims(claims: Auth0Claims): Auth0Session {
  if (!claims) {
    return null
  }

  let scopeDomain
  if (process.env.AUTH0_DEFAULT_AUDIENCE) {
    scopeDomain = `https://${process.env.AUTH0_DEFAULT_AUDIENCE}`
  } else {
    scopeDomain = 'https://paintscout.com'
  }

  return {
    user_id: claims.sub,
    email: claims.email,
    name: claims.name,
    picture: claims.picture,
    // @ts-ignore
    user_metadata: claims[`${scopeDomain}/user_metadata`],
    // @ts-ignore
    app_metadata: claims[`${scopeDomain}/app_metadata`],
    issued: claims.iat,
    expires: claims.exp
  }
}

/** Context consumers */
export const useAuth = () => useContext(AuthContext)
